/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { GridColDef, GridColumnGroup } from "@mui/x-data-grid-premium";
import { MaterialsGroupHeader } from "components/strategyDashboard/heats/MaterialsGroupHeader";
import { RequireNonAggregationRow } from "components/strategyDashboard/heats/RequireNonAggregationRow";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { UnitsFormatter } from "hooks/formatters";
import { NumberFormatter } from "hooks/serialisers/numbers";
import { TFunction } from "i18next";
import { useCallback } from "react";
import {
  ChemicalElementSymbol,
  FeatureFlag,
  HeatSeries,
  MaterialRead,
  NonElementQuantity,
  RecipeType,
  StrategyDashboardHeatData,
  UnitType,
} from "src/store/api/generatedApi";
import { HeatHeaderFilter } from "./HeatHeaderFilter";
import { HeatRowFilters, isHeatFilterField } from "./heatsRowFilter";
import { ChemistryViolationsCells } from "./cells/ChemistryViolationsCells";
import { ChemistryViolationsHeader } from "./cells/ChemistryViolationsHeader";
import { CostSparkline } from "./cells/CostSparkline";
import { HeatCostCell } from "./cells/HeatCostCell";
import { SteelGradeCell } from "./cells/SteelGradeCell";
import {
  ChargedPriceCell,
  PriceTrafficLightCell,
} from "./cells/PriceComparisonCell";
import { StrikeTrafficLightCell } from "./cells/StrikeTrafficLightCell";
import { TotalCostDiffCell } from "./cells/TotalCostDiffCell";
import { WatchHeatCell } from "./cells/WatchHeatCell";
import { useChemistryElement } from "./chemistryTab/ChemistryElementContext";
import { createRatioRecipeColumns } from "./ratioRecipeColumns";
import {
  createLoadingZoneColumns,
  createLoadingZoneColumnGroup,
} from "./loadingZoneColumns";
import { createNonComplianceReasonColumn } from "./nonComplianceReasonColumn";

const hasLoadingZoneLayout = (data: StrategyDashboardHeatData): boolean =>
  (data.loading_zone_layout?.length ?? 0) > 0;

const hasNonComplianceReasonColumn = (
  data: StrategyDashboardHeatData
): boolean =>
  data.series.some((heat) => heat.unavailable_required_materials != null);

const hasOperatorErrorRateColumn = (data: StrategyDashboardHeatData): boolean =>
  data.series.some((heat) => heat.operator_error_rate != null);

// The dashboard column surfaces the planned-vs-ScrapEye pair; other pairs are only
// shown in the layering tab.
const plannedVsScrapEyeOverall = (row: HeatSeries): number | null =>
  row.layering_compliance?.find(
    (pair) =>
      pair.target_mix_source === "plannedSource" &&
      pair.comparison_mix_source === "ScrapEye"
  )?.compliance.overall ?? null;

const createLayeringComplianceColumn = (
  t: TFunction,
  formatNumber: NumberFormatter
): GridColDef<HeatSeries> => ({
  field: "_layeringCompliance",
  headerName: t("layeringCompliance"),
  resizable: false,
  aggregable: false,
  width: COLUMN_WIDTHS.status,
  headerAlign: "center",
  align: "center",
  valueGetter: (_, row): number | null => plannedVsScrapEyeOverall(row),
  renderCell: RequireNonAggregationRow(({ row }) => {
    const overall = plannedVsScrapEyeOverall(row);
    if (overall == null) {
      return null;
    }
    return (
      <span>
        {t("compliancePercent", {
          percent: formatNumber(overall * 100, { decimalPlaces: 0 }),
        })}
      </span>
    );
  }),
});

const createOperatorErrorRateColumn = (
  t: TFunction,
  formatNumber: NumberFormatter
): GridColDef<HeatSeries> => ({
  field: "_operatorErrorRate",
  headerName: t("operatorErrorRate"),
  resizable: false,
  aggregable: false,
  width: COLUMN_WIDTHS.status,
  headerAlign: "center",
  align: "center",
  valueGetter: (_, row): number | null => row.operator_error_rate,
  renderCell: RequireNonAggregationRow(({ row }) => {
    const rate = row.operator_error_rate;
    if (rate == null) {
      return null;
    }
    return (
      <span>
        {t("compliancePercent", {
          percent: formatNumber(rate * 100, { decimalPlaces: 0 }),
        })}
      </span>
    );
  }),
});

const COLUMNS_REQUIRED_FOR_COLLAPSE = 3;

const COLUMN_WIDTHS = {
  heatId: 80,
  crewId: 80,
  eaf: 80,
  steelGrade: 165,
  recipeName: 120,
  sequence: 50,
  startTime: 88,
  tapTime: 88,
  summary: 110,
  mixSourceSuggested: 70,
  mixSource: 90,
  chemistryViolations: 100,
  sparkline: 160,
  cumulativeDiff: 140,
  status: 120,
} as const;

const columnHasNonNullValues = (
  data: StrategyDashboardHeatData,
  column: keyof HeatSeries
): boolean => {
  return data.series.some(
    (heat) => heat[column] !== null && heat[column] !== undefined
  );
};

const createBasicColumns = (
  t: TFunction,
  formatTime: (date: string) => string
): GridColDef<HeatSeries>[] => {
  return [
    {
      field: "heat_id",
      width: COLUMN_WIDTHS.heatId,
      headerName: "#",
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
      renderCell: (params) => (
        <Typography variant="body1" component="span">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "crew_id",
      width: COLUMN_WIDTHS.crewId,
      headerName: t("crew"),
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: true,
    },
    {
      field: "eaf",
      width: COLUMN_WIDTHS.eaf,
      headerName: t("eaf"),
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: true,
    },
    {
      field: "steel_grade",
      minWidth: COLUMN_WIDTHS.steelGrade,
      flex: 1,
      headerName: t("steelGrade"),
      renderCell: RequireNonAggregationRow(({ row }) => (
        <SteelGradeCell
          steelGrade={row.steel_grade}
          originalSteelGrade={row.original_steel_grade}
        />
      )),
      sortable: false,
      resizable: false,
    },
    {
      field: "recipe_name",
      width: COLUMN_WIDTHS.recipeName,
      headerName: t("recipe"),
      sortable: false,
      resizable: true,
    },
    {
      field: "sequence_number",
      width: COLUMN_WIDTHS.sequence,
      headerName: t("sequenceShort"),
      sortable: false,
      resizable: false,
      renderCell: (params) => (
        <Typography variant="body1" component="span">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "start_timestamp",
      width: COLUMN_WIDTHS.startTime,
      headerName: t("startNoun"),
      sortable: true,
      resizable: false,
      valueFormatter: (value) => (value ? formatTime(value) : null),
      renderCell: (params) =>
        params.formattedValue == null ? null : (
          <Typography variant="body1" component="span">
            {params.formattedValue}
          </Typography>
        ),
    },
    {
      field: "tap_timestamp",
      width: COLUMN_WIDTHS.tapTime,
      headerName: t("tap"),
      sortable: true,
      resizable: false,
      valueFormatter: (value) => (value ? formatTime(value) : null),
      renderCell: (params) =>
        params.formattedValue == null ? null : (
          <Typography variant="body1" component="span">
            {params.formattedValue}
          </Typography>
        ),
    },
  ];
};

const SUMMARIES_REQUIRED_FOR_COLLAPSE = 2;

const getSummaryEntries = (
  data: StrategyDashboardHeatData
): [string, UnitType | null][] =>
  Object.entries(data.available_summaries)
    .filter(([name]) => name !== "scrapEyePriceDiff")
    .sort(([aName], [bName]) => aName.localeCompare(bName));

const createSummaryColumns = (
  data: StrategyDashboardHeatData,
  t: TFunction,
  formatUnits: UnitsFormatter,
  formatNumber: NumberFormatter,
  isCollapsed: boolean
): GridColDef<HeatSeries>[] => {
  const summaryEntries = getSummaryEntries(data);

  if (isCollapsed && summaryEntries.length >= SUMMARIES_REQUIRED_FOR_COLLAPSE) {
    return [];
  }

  return summaryEntries.map(
    ([summaryName, unitType]): GridColDef<HeatSeries> => {
      const columnDecimalPlaces: number | undefined = (() => {
        for (const heat of data.series) {
          const s = heat.summary_data?.find((sd) => sd.name === summaryName);
          if (s?.decimal_places != null) return s.decimal_places;
        }
        return undefined;
      })();

      return {
        field: summaryName,
        type: "number",
        headerName:
          unitType === null
            ? t(summaryName)
            : `${t(summaryName)} ${formatUnits(unitType, false)}`,
        sortable: true,
        resizable: false,
        minWidth: COLUMN_WIDTHS.summary,
        flex: 1.2,
        valueGetter: (_, row): number | string | null => {
          const summary = row.summary_data?.find((s) => s.name == summaryName);
          return summary?.value ?? null;
        },
        valueFormatter: (value: number | null | string) => {
          if (value === null) return null;
          if (typeof value === "number") {
            return formatNumber(value, {
              decimalPlaces: columnDecimalPlaces,
            });
          }
          return value;
        },
        renderCell: (params) =>
          params.formattedValue == null ? null : (
            <Typography variant="body1" component="span">
              {params.formattedValue}
            </Typography>
          ),
      };
    }
  );
};

const createMixSourceColumns = (
  data: StrategyDashboardHeatData,
  t: TFunction,
  ignoredMaterials: Set<string>
): GridColDef<HeatSeries>[] => {
  const MIN_TOTAL_WIDTH = 200;
  const MIN_WIDTH_PER_SOURCE =
    MIN_TOTAL_WIDTH / data.available_mix_sources.length;

  const columns: GridColDef<HeatSeries>[] = data.available_mix_sources.map(
    (source) => ({
      field: source,
      headerName: t(source),
      minWidth: MIN_WIDTH_PER_SOURCE,
      width:
        source === "suggestedSource"
          ? COLUMN_WIDTHS.mixSourceSuggested
          : COLUMN_WIDTHS.mixSource,
      sortable: true,
      resizable: false,
      cellClassName: "heat-cost-cell",
      renderCell: RequireNonAggregationRow(({ row }) => (
        <HeatCostCell
          row={row}
          source={source}
          ignoredMaterials={ignoredMaterials}
        />
      )),
    })
  );

  return columns;
};

const createChemistryViolationsColumn = (
  data: StrategyDashboardHeatData | undefined,
  finalSampleOnly: boolean,
  setFinalSampleOnly: (finalSampleOnly: boolean) => void,
  t: TFunction
): GridColDef<HeatSeries> => {
  const multipleSamples =
    data?.series?.some((heat) => heat.samples.length > 1) ?? false;
  const availableConstraints = data?.available_sample_constraints ?? [];
  return {
    field: "chemistryViolations",
    minWidth: COLUMN_WIDTHS.chemistryViolations,
    flex: 2,
    renderHeader: () => (
      <ChemistryViolationsHeader
        multipleSamples={multipleSamples}
        finalSampleOnly={finalSampleOnly}
        setFinalSampleOnly={setFinalSampleOnly}
      />
    ),
    sortable: false,
    resizable: false,
    aggregable: false,
    renderCell: RequireNonAggregationRow(({ row }) => {
      const { setSelectedElement } = useChemistryElement();
      const { setSelectedTabKey } = useHeatsSelection();

      const handleElementClick = useCallback(
        (element: ChemicalElementSymbol | NonElementQuantity) => {
          setSelectedElement(element);
          setSelectedTabKey("chemistry");
        },
        [setSelectedElement, setSelectedTabKey]
      );
      return (
        <ChemistryViolationsCells
          row={row}
          t={t}
          availableConstraints={availableConstraints}
          handleElementClick={handleElementClick}
          finalSampleOnly={finalSampleOnly}
          wrap
        />
      );
    }),
  };
};

const createWatchHeatColumn = (): GridColDef<HeatSeries> => {
  return {
    field: "_watch",
    headerName: "",
    width: 40,
    sortable: false,
    resizable: false,
    cellClassName: "watch-heat-cell",
    renderCell: RequireNonAggregationRow(({ row }) => (
      <WatchHeatCell row={row} />
    )),
  };
};

const createMaterialsColumns = (
  data: StrategyDashboardHeatData,
  t: TFunction,
  tenantT: TFunction,
  formatNumber: NumberFormatter,
  isCollapsed: boolean,
  selectedSummaryName: string | null,
  ignoredMaterials: Set<string>
): GridColDef<HeatSeries>[] => {
  if (data.available_mix_sources.length === 0) {
    return [];
  }

  // Only show collapsed mode (sparkline/diff) if there are 3+ sources to compare
  const canCollapse =
    data.available_mix_sources.length >= COLUMNS_REQUIRED_FOR_COLLAPSE;

  if (isCollapsed && canCollapse) {
    return [
      {
        field: "_materialsSparkline",
        headerName: tenantT("trend"),
        sortable: false,
        resizable: false,
        aggregable: false,
        width: COLUMN_WIDTHS.sparkline,
        renderCell: RequireNonAggregationRow(({ row }) => (
          <CostSparkline
            row={row}
            sources={data.available_mix_sources}
            selectedSummaryName={selectedSummaryName}
            ignoredMaterials={ignoredMaterials}
            width={COLUMN_WIDTHS.sparkline - 30}
            height={20}
          />
        )),
      },
      {
        field: "_materialsDiff",
        headerName: tenantT("cumulativeDiff"),
        sortable: false,
        resizable: false,
        aggregable: false,
        width: COLUMN_WIDTHS.cumulativeDiff,
        renderCell: RequireNonAggregationRow(({ row }) => (
          <TotalCostDiffCell
            row={row}
            sources={data.available_mix_sources}
            selectedSummaryName={selectedSummaryName}
            ignoredMaterials={ignoredMaterials}
            formatNumber={formatNumber}
          />
        )),
      },
    ];
  }

  return createMixSourceColumns(data, t, ignoredMaterials);
};

const createPriceComparisonColumns = (
  t: TFunction,
  tenantT: TFunction,
  formatNumber: NumberFormatter,
  ignoredMaterials: Set<string>
): GridColDef<HeatSeries>[] => {
  return [
    {
      field: "_plannedChargedPrice",
      headerName: t("plannedSource"),
      sortable: false,
      resizable: false,
      aggregable: false,
      width: COLUMN_WIDTHS.mixSource,
      headerAlign: "center",
      align: "center",
      renderCell: RequireNonAggregationRow(({ row }) => (
        <ChargedPriceCell
          row={row}
          source="plannedSource"
          ignoredMaterials={ignoredMaterials}
          formatNumber={formatNumber}
        />
      )),
    },
    {
      field: "_scrapEyeChargedPrice",
      headerName: t("scrapEye"),
      sortable: false,
      resizable: false,
      aggregable: false,
      width: COLUMN_WIDTHS.mixSource,
      headerAlign: "center",
      align: "center",
      renderCell: RequireNonAggregationRow(({ row }) => (
        <ChargedPriceCell
          row={row}
          source="ScrapEye"
          ignoredMaterials={ignoredMaterials}
          formatNumber={formatNumber}
        />
      )),
    },
    {
      field: "_priceTrafficLight",
      headerName: tenantT("costCompliance"),
      sortable: false,
      resizable: false,
      aggregable: false,
      width: COLUMN_WIDTHS.status,
      renderCell: RequireNonAggregationRow(({ row }) => (
        <PriceTrafficLightCell row={row} />
      )),
    },
  ];
};

const createStrikeColumns = (
  tenantT: TFunction,
  ignoredMaterials: Set<string>
): GridColDef<HeatSeries>[] => {
  return [
    {
      field: "_strikes",
      headerName: tenantT("compliance"),
      sortable: false,
      resizable: false,
      aggregable: false,
      width: COLUMN_WIDTHS.status,
      renderCell: RequireNonAggregationRow(({ row }) => (
        <StrikeTrafficLightCell row={row} ignoredMaterials={ignoredMaterials} />
      )),
    },
  ];
};

export const withHeaderFilters = (
  columns: GridColDef<HeatSeries>[],
  options: HeatRowFilters
): GridColDef<HeatSeries>[] =>
  columns.map((column) => {
    const field = column.field;
    if (!isHeatFilterField(field)) {
      return column;
    }
    const label = column.headerName ?? field;
    return {
      ...column,
      renderHeader: () => (
        <HeatHeaderFilter
          field={field}
          label={label}
          options={options[field]}
        />
      ),
    };
  });

export const makeColumns = (
  data: StrategyDashboardHeatData,
  materials: MaterialRead[],
  t: TFunction,
  tenantT: TFunction,
  featureFlags: FeatureFlag[],
  formatTime: (date: string) => string,
  formatUnits: UnitsFormatter,
  formatNumber: NumberFormatter,
  ignoredMaterials: Set<string>,
  scrapEyeOnly: boolean,
  hasPriceComparison: boolean,
  finalSampleOnly: boolean,
  setFinalSampleOnly: (finalSampleOnly: boolean) => void,
  isCollapsed = false,
  selectedSummaryName: string | null = null,
  isSummaryCollapsed = false,
  recipeType?: RecipeType
): GridColDef<HeatSeries>[] => {
  const availableMixSources = scrapEyeOnly
    ? ["ScrapEye"]
    : data.available_mix_sources;
  const strikeRules = new Map(
    (data.strike_rules ?? []).map((r) => [r.material_name, r])
  );
  const baseColumns = [
    ...(featureFlags.includes("scrap_eyes") ? [createWatchHeatColumn()] : []),
    ...createBasicColumns(tenantT, formatTime),
    ...createSummaryColumns(
      data,
      t,
      formatUnits,
      formatNumber,
      isSummaryCollapsed
    ),
    ...(recipeType === "ratio"
      ? createRatioRecipeColumns(
          t,
          availableMixSources,
          materials,
          scrapEyeOnly,
          ignoredMaterials,
          strikeRules
        )
      : []),
  ];

  const mixSummaryColumns = scrapEyeOnly
    ? [
        ...(hasPriceComparison
          ? createPriceComparisonColumns(
              t,
              tenantT,
              formatNumber,
              ignoredMaterials
            ).slice(0, 2)
          : []),
        ...createStrikeColumns(tenantT, ignoredMaterials),
        ...(hasNonComplianceReasonColumn(data)
          ? [createNonComplianceReasonColumn(t, ignoredMaterials)]
          : []),
      ]
    : hasPriceComparison
      ? createPriceComparisonColumns(t, tenantT, formatNumber, ignoredMaterials)
      : createMaterialsColumns(
          data,
          t,
          tenantT,
          formatNumber,
          isCollapsed,
          selectedSummaryName,
          ignoredMaterials
        );

  return [
    ...baseColumns,
    ...(hasLoadingZoneLayout(data)
      ? createLoadingZoneColumns(data, materials, t)
      : []),
    ...mixSummaryColumns,
    ...(featureFlags.includes("bucket_layering_compliance")
      ? [createLayeringComplianceColumn(t, formatNumber)]
      : []),
    ...(featureFlags.includes("operator_error_rate") &&
    hasOperatorErrorRateColumn(data)
      ? [createOperatorErrorRateColumn(t, formatNumber)]
      : []),
    createChemistryViolationsColumn(
      data,
      finalSampleOnly,
      setFinalSampleOnly,
      tenantT
    ),
  ];
};

export const makeColumnVisibilityModel = (
  data: StrategyDashboardHeatData
): Record<string, boolean> => {
  return {
    crew_id: columnHasNonNullValues(data, "crew_id"),
    eaf: columnHasNonNullValues(data, "eaf"),
    recipe_name: columnHasNonNullValues(data, "recipe_name"),
    sequence_number: columnHasNonNullValues(data, "sequence_number"),
  };
};

export const makeColumnGroupingModel = (
  data: StrategyDashboardHeatData,
  t: TFunction,
  formatUnits: UnitsFormatter,
  selectedSummaryName: string | null,
  onSummaryChange: (summaryName: string) => void,
  isCollapsed: boolean,
  onToggleCollapse: () => void,
  hasPriceComparison: boolean,
  isSummaryCollapsed: boolean,
  onToggleSummaryCollapse: () => void,
  scrapEyeOnly: boolean
): GridColumnGroup[] => {
  const groups: GridColumnGroup[] = [];

  const summaryEntries = getSummaryEntries(data);
  if (
    !isSummaryCollapsed &&
    summaryEntries.length >= SUMMARIES_REQUIRED_FOR_COLLAPSE
  ) {
    groups.push({
      groupId: "summary",
      renderHeaderGroup: () => (
        <SummaryGroupHeader
          isCollapsed={isSummaryCollapsed}
          onToggleCollapse={onToggleSummaryCollapse}
        />
      ),
      freeReordering: true,
      children: summaryEntries.map(([name]) => ({ field: name })),
    });
  }

  if (hasLoadingZoneLayout(data)) {
    groups.push(createLoadingZoneColumnGroup(data, t));
  }

  if (data.available_mix_sources.length > 0) {
    if (hasPriceComparison) {
      groups.push({
        groupId: t("materials"),
        renderHeaderGroup: () => (
          <PriceComparisonGroupHeader
            t={t}
            formatUnits={formatUnits}
            isSummaryCollapsed={isSummaryCollapsed}
            onToggleSummaryCollapse={onToggleSummaryCollapse}
          />
        ),
        freeReordering: true,
        children: scrapEyeOnly
          ? [
              { field: "_plannedChargedPrice" },
              { field: "_scrapEyeChargedPrice" },
            ]
          : [
              { field: "_plannedChargedPrice" },
              { field: "_scrapEyeChargedPrice" },
              { field: "_priceTrafficLight" },
            ],
      });
    } else if (!scrapEyeOnly) {
      const canCollapse =
        data.available_mix_sources.length >= COLUMNS_REQUIRED_FOR_COLLAPSE;

      const children =
        isCollapsed && canCollapse
          ? [{ field: "_materialsSparkline" }, { field: "_materialsDiff" }]
          : data.available_mix_sources.map((source) => ({ field: source }));

      groups.push({
        groupId: t("materials"),
        renderHeaderGroup: () => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isSummaryCollapsed ? (
              <IconButton
                size="small"
                onClick={onToggleSummaryCollapse}
                sx={{
                  p: 0.25,
                  color: "text.secondary",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <ChevronRight sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
            <MaterialsGroupHeader
              availableSummaries={data.available_mix_summaries}
              selectedSummaryName={selectedSummaryName}
              onSummaryChange={onSummaryChange}
              isCollapsed={isCollapsed}
              onToggleCollapse={onToggleCollapse}
              canCollapse={canCollapse}
            />
          </Box>
        ),
        freeReordering: true,
        children,
      });
    }
  }

  return groups;
};

const PriceComparisonGroupHeader = ({
  t,
  formatUnits,
  isSummaryCollapsed,
  onToggleSummaryCollapse,
}: {
  t: TFunction;
  formatUnits: UnitsFormatter;
  isSummaryCollapsed: boolean;
  onToggleSummaryCollapse: () => void;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      height: "100%",
      px: 1,
      py: 0.5,
      gap: 0.5,
    }}
  >
    {isSummaryCollapsed ? (
      <IconButton
        size="small"
        onClick={onToggleSummaryCollapse}
        sx={{
          p: 0.25,
          color: "text.secondary",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>
    ) : null}
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {`${t("chargedPrice")}${formatUnits("specific_cost")}`}
    </Typography>
  </Box>
);

const SummaryGroupHeader = ({
  isCollapsed,
  onToggleCollapse,
}: {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      height: "100%",
      px: 0.5,
      py: 0.5,
    }}
  >
    <IconButton
      size="small"
      onClick={onToggleCollapse}
      sx={{
        p: 0.25,
        color: "text.secondary",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
    </IconButton>
  </Box>
);
